/**
 * Outlet binding — first-launch UX (Sprint 2 · Part 1).
 *
 * Rendered when a POS device has no `pos_terminal_binding` yet. Lists
 * the tenant's outlets that have the `pos` feature enabled and lets
 * the operator commit one binding. After confirmation the device is
 * locked to that outlet until an outlet_admin rebinds.
 *
 * The screen uses the platform JWT of the tenant admin who is
 * commissioning the device — the same auth surface as PosSettings.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { setBinding } from "./terminalBinding";
import { Store, ShieldAlert, LogOut, ArrowRight } from "lucide-react";

export default function OutletBindingScreen({ onBound, onExit }) {
  const { user, API, getHeaders } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Outlets carry per-outlet feature overrides; the tenant-level
        // `pos` flag lives on /api/tenant/features. An outlet is
        // eligible only when the tenant-level `pos` is on AND the
        // outlet-level override (if any) is not explicitly off.
        const [feats, list] = await Promise.all([
          axios.get(`${API}/tenant/features`, { headers: getHeaders() }),
          axios.get(`${API}/outlets`,          { headers: getHeaders() }),
        ]);
        if (!alive) return;
        setFeatures(feats.data || {});
        setOutlets(list.data || []);
      } catch (e) {
        if (!alive) return;
        setError("Could not load outlets. Sign in again or contact support.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [API, getHeaders]);

  const tenantPosOn = features.pos !== false && features.commerce_home !== false;

  const bindTo = async (o) => {
    setBusy(o.id);
    try {
      const doc = setBinding({
        tenant_id: user?.tenant_id || features?.tenant_id || "default",
        outlet_id: o.id,
        outlet_name: o.name,
      });
      onBound?.(doc);
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F1A16] flex items-center justify-center px-6 py-8 overflow-auto"
         data-testid="pos-outlet-binding-screen">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-7 flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" /> POS device setup
            </div>
            <h1 className="text-[22px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] mt-1">Bind this terminal to an outlet</h1>
            <p className="text-sm text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1">
              A terminal is a physical device in one outlet. Pick the outlet this device belongs to —
              staff PINs from other outlets will be refused after binding. Rebinding later requires an
              outlet-admin PIN and no live shift.
            </p>
          </div>
          {onExit ? (
            <button type="button" onClick={onExit}
              data-testid="pos-outlet-binding-cancel"
              className="inline-flex items-center gap-1 text-sm text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)]">
              <LogOut className="w-4 h-4" /> Exit
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="py-10 text-center text-[color:var(--pos-color-secondary-text,#5C6B64)]">Loading outlets…</div>
        ) : error ? (
          <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        ) : !tenantPosOn ? (
          <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm">
            POS is not enabled on this tenant. Ask a super-admin to enable it before binding a device.
          </div>
        ) : outlets.length === 0 ? (
          <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] text-[color:var(--pos-color-primary-text,#1A2421)] p-3 text-sm">
            No outlets available. Add an outlet first, then return here.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {outlets.map((o) => (
              <button key={o.id}
                type="button"
                disabled={busy !== null}
                onClick={() => bindTo(o)}
                data-testid={`pos-bind-outlet-${o.id}`}
                className="text-left rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[#F1F5F3] hover:border-[color:var(--color-primary)] p-3 flex items-center justify-between gap-2 transition disabled:opacity-50 disabled:cursor-wait">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] truncate">{o.name}</div>
                  <div className="text-xs text-[color:var(--pos-color-secondary-text,#5C6B64)] truncate">
                    {[o.city, o.address_line1].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[color:var(--color-primary)] shrink-0" />
              </button>
            ))}
          </div>
        )}
        <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] leading-relaxed border-t border-[color:var(--pos-color-border,#EEF1EE)] pt-3">
          You must be signed in as a tenant administrator to bind a device. Existing bindings on this
          browser survive log-out, terminal lock and browser restart.
        </div>
      </div>
    </div>
  );
}
